import { ConfigService } from '@maia/config';
import { BaseService } from '@maia/feature-utils';
import { HttpService } from '@maia/httpClient';
import { CreateCartDto } from '../../dtos/cart.dto';
import { IGetCartEntity, IPostCartEntity } from '../../entitites/cart.entity';
import { ICompositionEntity } from '../../entitites/composition.entity';
import { IProductEntity } from '../../entitites/product.entity';
import { IProductQuestionEntity } from '../../entitites/productQuestion.entity';
import { PutQuestionDto } from '../../dtos/question/question.dto';
import { IUpsellproductsEntity } from '../../entitites/upsellProducts';

export class CartService extends BaseService {
  constructor(httpService: HttpService, configService: ConfigService) {
    super(httpService, configService);
  }

  public async getUpsellproducts({ customerGuid }: { customerGuid: string }) {
    return await this.get<Array<IUpsellproductsEntity>>(
      `customers/${customerGuid}/upsellproducts`,
      'upsellproducts',
    );
  }

  public async createCart({
    customerGuid,
    body,
  }: {
    customerGuid: string;
    body: ConstructorParameters<typeof CreateCartDto>[number];
  }) {
    return await this.post<IPostCartEntity, CreateCartDto>(
      `customers/${customerGuid}/carts`,
      new CreateCartDto(body),
      'Create cart',
    );
  }

  public async getCart({ cartGuid }: { cartGuid: string }) {
    return await this.get<IGetCartEntity>(`carts/${cartGuid}`, 'get cart');
  }

  public async getProducts({
    customerGuid,
    cartGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
  }) {
    return await this.get<Array<IProductEntity>>(
      `customers/${customerGuid}/carts/${cartGuid}/products`,
      'Get products',
    );
  }

  public async getQuestions({
    customerGuid,
    cartGuid,
    productGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
    productGuid: string;
  }) {
    return await this.get<Array<IProductQuestionEntity>>(
      `customers/${customerGuid}/carts/${cartGuid}/products/${productGuid}/questions`,
      'Get questions',
    );
  }

  public async putQuestions({
    customerGuid,
    cartGuid,
    productGuid,
    questions,
  }: {
    customerGuid: string;
    cartGuid: string;
    productGuid: string;
    questions: ConstructorParameters<typeof PutQuestionDto>[number];
  }) {
    return await this.put<
      {},
      ConstructorParameters<typeof PutQuestionDto>[number]
    >(
      `customers/${customerGuid}/carts/${cartGuid}/products/${productGuid}/questions`,
      new PutQuestionDto(questions),
      'Put questions',
    );
  }

  public async getCompositions({
    customerGuid,
    cartGuid,
    productGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
    productGuid: string;
  }) {
    return await this.get<Array<ICompositionEntity>>(
      `customers/${customerGuid}/carts/${cartGuid}/products/${productGuid}/compositions`,
      'Get compositions',
    );
  }

  public async putCompositions({
    customerGuid,
    cartGuid,
    productGuid,
    compositionGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
    productGuid: string;
    compositionGuid: string;
  }) {
    return await this.put<{ selected: boolean }, {}>(
      `customers/${customerGuid}/carts/${cartGuid}/products/${productGuid}/compositions/${compositionGuid}`,
      {},
      'Put compositions',
    );
  }

  public async getCompositionQuestions({
    customerGuid,
    cartGuid,
    productGuid,
  }: {
    customerGuid: string;
    cartGuid: string;
    productGuid: string;
  }) {
    return await this.get<IProductEntity>(
      `customers/${customerGuid}/carts/${cartGuid}/products/${productGuid}`,
      'Get composition questions',
    );
  }

  public async submitCart(params: {
    customerGuid: string;
    cartGuid: string;
    type: 'quotation' | 'order' | 'special';
    description?: string;
  }) {
    const { cartGuid, customerGuid, type, description } = params;
    return await this.put<unknown, {}>(
      `customers/${customerGuid}/carts/${cartGuid}/submit`,
      {
        type,
        description,
      },
      'Submit',
    );
  }
}
